/** Relative time bounds: `now`, `now-15m`, `now+2d`.
 *
 * The backend (`apiv2/lib/console/time_bounds.py`) resolves these on every request,
 * which is what keeps a "last 15 minutes" grid moving on refresh. The client resolves
 * them only where a value must be frozen: one-shot forms, and a batch run over "all
 * matching" rows. Resolution mirrors the backend exactly: UTC arithmetic (months clamp
 * to the month's last day, as dateutil's relativedelta does), then the minute the
 * instant falls in.
 */
import type { RelativeTime, TimeBound, TimeWindowUnit, TimeWindowValue } from './types';

const UNIT_CODE: Record<TimeWindowUnit, string> = {
  minutes: 'm',
  hours: 'h',
  days: 'd',
  weeks: 'w',
  months: 'M',
};

const CODE_UNIT: Record<string, TimeWindowUnit> = {
  m: 'minutes',
  h: 'hours',
  d: 'days',
  w: 'weeks',
  M: 'months',
};

const UNIT_MS: Record<Exclude<TimeWindowUnit, 'months'>, number> = {
  minutes: 60_000,
  hours: 3_600_000,
  days: 86_400_000,
  weeks: 7 * 86_400_000,
};

/** The largest relative amount the language allows (backend: N = 1…9999). */
export const MAX_RELATIVE_AMOUNT = 9999;

const RELATIVE_RE = /^now(?:([+-])([1-9]\d{0,3})([mhdwM]))?$/;

export const NOW: RelativeTime = { offset: 0, unit: 'minutes' };

export function isRelative(bound: TimeBound | null | undefined): bound is RelativeTime {
  return bound != null && !(bound instanceof Date);
}

export function isNow(bound: TimeBound | null | undefined): boolean {
  return isRelative(bound) && bound.offset === 0;
}

/** `now-15m` → {offset: −15, unit: 'minutes'}; null for anything that isn't a token. */
export function parseRelative(value: string): RelativeTime | null {
  const m = RELATIVE_RE.exec(value);
  if (!m) return null;
  if (!m[1]) return NOW;
  const amount = Number(m[2]);
  return { offset: m[1] === '-' ? -amount : amount, unit: CODE_UNIT[m[3]!]! };
}

export function formatRelative(rt: RelativeTime): string {
  if (rt.offset === 0) return 'now';
  return `now${rt.offset < 0 ? '-' : '+'}${Math.abs(rt.offset)}${UNIT_CODE[rt.unit]}`;
}

function addUtcMonths(d: Date, months: number): Date {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + months;
  const targetY = y + Math.floor(m / 12);
  const targetM = ((m % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetY, targetM + 1, 0)).getUTCDate();
  const out = new Date(d.getTime());
  out.setUTCFullYear(targetY, targetM, Math.min(d.getUTCDate(), lastDay));
  return out;
}

function floorToMinute(d: Date): Date {
  return new Date(Math.floor(d.getTime() / 60_000) * 60_000);
}

/** The start of the minute `now + offset·unit` falls in — what the backend compares
 *  against. A relative lower bound covers from here; an upper bound covers through the
 *  end of this minute. */
export function relativeMinute(rt: RelativeTime, now: Date = new Date()): Date {
  const t =
    rt.unit === 'months'
      ? addUtcMonths(now, rt.offset)
      : new Date(now.getTime() + rt.offset * UNIT_MS[rt.unit]);
  return floorToMinute(t);
}

/** Freeze a window to instants, for one-shot consumers. From is the start of its
 *  minute; a relative To is the end of its minute (exclusive), so the frozen window
 *  never loses the rows the live one would have shown. Fixed bounds pass through. */
export function resolveTimeWindow(
  value: TimeWindowValue,
  now: Date = new Date(),
): [Date | null, Date | null] {
  const [from, to] = value;
  return [
    from === null ? null : isRelative(from) ? relativeMinute(from, now) : from,
    to === null ? null : isRelative(to) ? new Date(relativeMinute(to, now).getTime() + 60_000) : to,
  ];
}

function unitWord(amount: number, unit: TimeWindowUnit): string {
  return amount === 1 ? unit.slice(0, -1) : unit;
}

/** "last 15 minutes" / "next 2 days" for the windows the relative presets make
 *  ([now−N, now] and [now, now+N]); null for any other shape. */
export function relativeWindowPhrase(from: TimeBound | null, to: TimeBound | null): string | null {
  if (isRelative(from) && isNow(to) && from.offset < 0) {
    return `last ${-from.offset} ${unitWord(-from.offset, from.unit)}`;
  }
  if (isNow(from) && isRelative(to) && to.offset > 0) {
    return `next ${to.offset} ${unitWord(to.offset, to.unit)}`;
  }
  return null;
}

/** "15 minutes ago" / "2 days from now" / "now" — one relative bound on its own. */
export function relativeBoundPhrase(rt: RelativeTime): string {
  if (rt.offset === 0) return 'now';
  const n = Math.abs(rt.offset);
  return `${n} ${unitWord(n, rt.unit)} ${rt.offset < 0 ? 'ago' : 'from now'}`;
}
